<div class="flex flex-col gap-3">
  <div
    class="flex min-h-10 flex-wrap items-center justify-between gap-3"
    data-testid="campaign-details-kpi-goals-description"
  >
    <div class="flex items-center gap-2">
      @if (hasTable()) {
        @if (showLensSwitch()) {
          <app-segmented-control data-testid="campaign-details-goals-lens" [options]="lensOptions()" [(value)]="lens" />
        }
        <button
          mat-icon-button
          type="button"
          class="mat-secondary-transparent-button-color"
          data-testid="campaign-details-goals-legend-button"
          [attr.aria-label]="'pages.campaignDetails.goals.legend.button' | translate"
          [attr.aria-expanded]="legendOpen()"
          [floatUi]="legendPopover"
          [placement]="floatUiPlacementBottomStart"
          [showTrigger]="floatUiTriggerClick"
          appendTo="body"
          [hideOnClickOutside]="true"
          (onShown)="legendOpen.set(true)"
          (onHidden)="legendOpen.set(false)"
        >
          <app-icon src="/icons/info-circle.svg" alt="" color="var(--shikenso-purple-base-60)" [size]="16" />
        </button>
      }
    </div>
    <div class="flex items-center gap-4">
      @if (hasTable() && hiddenColumnCount()) {
        <span class="text-xs text-shikenso-purple-base-60" data-testid="campaign-details-goals-hidden-columns">
          {{
            (hiddenColumnCount() === 1 ? 'shared.matrixGrid.columnHidden' : 'shared.matrixGrid.columnsHidden')
              | translate: { count: hiddenColumnCount() }
          }}
          ·
          <button
            type="button"
            class="font-medium text-shikenso-pink-base hover:underline focus-visible:underline"
            data-testid="campaign-details-goals-show-all-columns"
            (click)="showAllColumns()"
          >
            {{ 'shared.matrixGrid.showAll' | translate }}
          </button>
        </span>
      }
      @if (!exportMode()) {
        <button
          mat-flat-button
          color="secondary"
          type="button"
          class="shrink-0"
          data-testid="campaign-details-goals-export"
          [disabled]="loading() || !gridRows().length"
          (click)="exportCsv()"
        >
          <div class="flex items-center gap-2">
            <app-icon src="/icons/download.svg" alt="" [size]="16" />
            {{ 'pages.campaignDetails.goals.export' | translate }}
          </div>
        </button>
      }
    </div>
  </div>

  @if (loadError()) {
    <p class="py-10 text-center text-sm text-shikenso-purple-base-60" data-testid="campaign-details-goals-error">
      {{ 'pages.campaignDetails.goals.loadError' | translate }}
    </p>
  } @else if (!loading() && !gridRows().length) {
    <p class="py-10 text-center text-sm text-shikenso-purple-base-60" data-testid="campaign-details-goals-empty">
      {{ 'pages.campaignDetails.goals.empty' | translate }}
    </p>
  } @else {
    <app-matrix-grid
      #grid
      data-testid="campaign-details-goals-matrix"
      [ariaLabel]="'pages.campaignDetails.goals.matrixLabel' | translate"
      [columns]="gridColumns()"
      [rows]="gridRows()"
      [loading]="loading()"
      [collapsedColumnIds]="collapsedColumnIds()"
      [sort]="gridSort()"
      [(expandedRowIds)]="expandedRowIds"
      (collapsedColumnIdsChange)="onCollapsedColumnIdsChange($event)"
    >
      <ng-template matrixHeaderDef let-column>
        @switch (column.kind) {
          @case ('creator') {
            <div class="flex min-w-0 items-center gap-2">
              <button
                mat-icon-button
                type="button"
                class="mat-secondary-transparent-button-color -my-2 -ml-2 shrink-0"
                data-testid="campaign-details-goals-expand-all"
                [attr.aria-expanded]="allRowsExpanded()"
                [attr.aria-label]="
                  (allRowsExpanded()
                    ? 'pages.campaignDetails.goals.header.collapseAll'
                    : 'pages.campaignDetails.goals.header.expandAll'
                  ) | translate
                "
                [matTooltip]="
                  (allRowsExpanded()
                    ? 'pages.campaignDetails.goals.header.collapseAll'
                    : 'pages.campaignDetails.goals.header.expandAll'
                  ) | translate
                "
                (click)="allRowsExpanded() ? grid.collapseAllRows() : grid.expandAllRows()"
              >
                <app-icon
                  src="/icons/chevrons-right.svg"
                  alt=""
                  class="block transition-transform"
                  [class.rotate-90]="allRowsExpanded()"
                  [color]="allRowsExpanded() ? 'var(--shikenso-pink-base)' : 'var(--shikenso-purple-base)'"
                  [size]="16"
                />
              </button>
              <!-- Avatar width: "Creator" starts where the names do. -->
              <span class="w-7 shrink-0" aria-hidden="true"></span>
              <button
                type="button"
                class="flex items-center gap-1 whitespace-nowrap text-xs text-shikenso-purple-base-60 hover:text-shikenso-purple-base"
                data-testid="campaign-details-goals-sort-creator"
                (click)="onSort(EGoalsSortKey.creator)"
              >
                {{ 'pages.campaignDetails.goals.header.creator' | translate }}
                <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: EGoalsSortKey.creator }" />
              </button>
            </div>
          }
          @case ('plan') {
            <button
              type="button"
              class="flex items-center gap-1 whitespace-nowrap text-left text-xs text-shikenso-purple-base-60 hover:text-shikenso-purple-base"
              data-testid="campaign-details-goals-sort-plan"
              (click)="onSort(EGoalsSortKey.planComplete)"
            >
              {{
                (lens() === EGoalsLens.kpis
                  ? 'pages.campaignDetails.goals.header.goalsReached'
                  : 'pages.campaignDetails.goals.header.planComplete'
                ) | translate
              }}
              <ng-container *ngTemplateOutlet="sortIcon; context: { $implicit: EGoalsSortKey.planComplete }" />
            </button>
          }
          @case ('placeholder') {
            <!-- Same line heights as a group header (name + sub text), so the header keeps its height. -->
            <div class="flex flex-col gap-0.5" data-testid="campaign-details-goals-placeholder-header">
              <div class="flex h-5 w-2/3 items-center">
                <ngx-skeleton-loader class="w-full" [theme]="{ height: '14px', margin: '0', width: '100%' }" />
              </div>
              <div class="flex h-4 w-1/3 items-center">
                <ngx-skeleton-loader class="w-full" [theme]="{ height: '12px', margin: '0', width: '100%' }" />
              </div>
            </div>
          }
          @default {
            @let header = columnHeaders().get(column.id);
            <!-- Optional groups: their bonus rows are their deliveries; other groups only what the blocks count. -->
            @let groupWide = column.group?.frequency === 'optional';
            <!-- w-0 + min-w-full: long names and KPI lists truncate in the column instead of widening it. -->
            <div class="flex w-0 min-w-full items-start gap-2" data-testid="campaign-details-goals-column-header">
              <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                <div class="flex min-w-0 items-baseline gap-2">
                  <span class="truncate text-sm font-medium" [matTooltip]="column.label">{{ column.label }}</span>
                  @if (header && canOpenDeliverablesLink({ ctaId: column.id, groupWide })) {
                    <button
                      type="button"
                      class="shrink-0 whitespace-nowrap text-xs text-shikenso-purple-base-60 hover:text-shikenso-purple-base hover:underline focus-visible:underline"
                      data-testid="campaign-details-goals-frequency-deliverables-link"
                      (click)="openDeliverables({ ctaId: column.id, groupWide })"
                    >
                      {{
                        'pages.campaignDetails.goals.frequency.' + header.frequency.key
                          | translate: header.frequency.params
                      }}
                    </button>
                  } @else if (header) {
                    <span class="shrink-0 whitespace-nowrap text-xs text-shikenso-purple-base-60">
                      {{
                        'pages.campaignDetails.goals.frequency.' + header.frequency.key
                          | translate: header.frequency.params
                      }}
                    </span>
                  }
                </div>
                @if (lens() === EGoalsLens.deliverables && canOpenDeliverablesLink({ ctaId: column.id, groupWide })) {
                  <button
                    type="button"
                    class="truncate text-left text-xs text-shikenso-purple-base-60 hover:text-shikenso-purple-base hover:underline focus-visible:underline"
                    data-testid="campaign-details-goals-header-deliverables-link"
                    [matTooltip]="header?.subTooltip ?? ''"
                    (click)="openDeliverables({ ctaId: column.id, groupWide })"
                  >
                    {{ header?.subText }}
                  </button>
                } @else {
                  <span
                    class="truncate text-xs text-shikenso-purple-base-60"
                    data-testid="campaign-details-goals-header-sub-text"
                    [matTooltip]="header?.subTooltip ?? ''"
                  >
                    {{ header?.subText }}
                  </span>
                }
              </div>
              <button
                mat-icon-button
                type="button"
                class="mat-secondary-transparent-button-color -my-2 shrink-0"
                data-testid="campaign-details-goals-column-menu"
                [attr.aria-label]="'pages.campaignDetails.goals.header.columnMenu' | translate: { name: column.label }"
                [matMenuTriggerFor]="columnMenu"
              >
                <app-icon src="/icons/three-dots.svg" alt="" [size]="16" />
              </button>
              <mat-menu #columnMenu="matMenu">
                <button mat-menu-item type="button" (click)="grid.collapseColumn(column.id)">
                  {{ 'pages.campaignDetails.goals.header.collapse' | translate }}
                </button>
                <button mat-menu-item type="button" (click)="grid.focusColumn(column.id)">
                  {{ 'pages.campaignDetails.goals.header.focus' | translate }}
                </button>
              </mat-menu>
            </div>
          }
        }
      </ng-template>

      <ng-template matrixCellDef let-row let-column="column" let-expanded="expanded" let-collapsed="collapsed">
        @switch (column.kind) {
          @case ('creator') {
            <div class="flex min-w-0 items-center gap-2">
              <button
                mat-icon-button
                type="button"
                class="mat-secondary-transparent-button-color -my-2 -ml-2 shrink-0"
                data-testid="campaign-details-goals-row-toggle"
                [attr.aria-expanded]="expanded"
                [attr.aria-label]="'pages.campaignDetails.goals.cell.expandRow' | translate: { name: row.creatorName }"
                (click)="grid.toggleRow(row.id)"
              >
                <app-icon
                  src="/icons/chevron-right.svg"
                  alt=""
                  class="block transition-transform"
                  [class.rotate-90]="expanded"
                  [size]="16"
                />
              </button>
              <app-icon-badge
                [content]="row.creatorIcon"
                [fallbackText]="row.creatorName"
                [size]="28"
                [borderRadius]="100"
              />
              <span class="min-w-0 truncate text-sm font-medium" [matTooltip]="row.creatorName">{{
                row.creatorName
              }}</span>
            </div>
          }
          @case ('plan') {
            @let progress = row.planProgress[lens()];
            @let planComplete = progress.total > 0 && progress.complete === progress.total;
            <div class="flex items-center gap-2 text-sm" data-testid="campaign-details-goals-plan-progress">
              <span
                class="h-2 w-2 shrink-0 rounded-full"
                data-testid="campaign-details-goals-plan-dot"
                [attr.data-complete]="planComplete"
                [style.background]="planComplete ? getBarToneBackground('success') : null"
                [style.border]="planComplete ? null : OPEN_DOT_BORDER"
              ></span>
              @if (lens() === EGoalsLens.deliverables && canOpenDeliverablesLink({ row })) {
                <button
                  type="button"
                  class="tabular-nums hover:underline focus-visible:underline"
                  data-testid="campaign-details-goals-plan-deliverables-link"
                  (click)="openDeliverables({ row })"
                >
                  {{ progress.complete }}/{{ progress.total }}
                </button>
              } @else {
                <span class="tabular-nums">{{ progress.complete }}/{{ progress.total }}</span>
              }
            </div>
          }
          @default {
            @let cell = row.cells[column.id];
            @let view = cellViews().get(cellKey(cell));
            @let hasBar = cell.kind !== 'notInPlan' && cell.kind !== 'optional' && cell.lenses[lens()].hasGoals;
            @if (collapsed) {
              @if (hasBar) {
                <span
                  role="img"
                  class="block h-2 w-2 rounded-full"
                  data-testid="campaign-details-goals-collapsed-dot"
                  [attr.data-complete]="cell.lenses[lens()].isComplete"
                  [attr.aria-label]="view?.summary ? cell.ctaName + ': ' + view?.summary : cell.ctaName"
                  [matTooltip]="view?.summary ?? ''"
                  [style.background]="cell.lenses[lens()].isComplete ? getBarToneBackground('success') : null"
                  [style.border]="cell.lenses[lens()].isComplete ? null : OPEN_DOT_BORDER"
                ></span>
              }
            } @else if (cell.kind === 'notInPlan') {
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.campaignDetails.goals.cell.notInPlan' | translate }}
              </span>
            } @else if (cell.kind === 'optional') {
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.campaignDetails.goals.frequency.optional' | translate }} ·
                @if (cell.optionalDelivered && canOpenContents()) {
                  <button
                    type="button"
                    class="hover:text-shikenso-purple-base hover:underline focus-visible:underline"
                    data-testid="campaign-details-goals-optional-link"
                    (click)="
                      openContents(row, cell.ctaId, { matchingTypes: [EMatchingType.matched, EMatchingType.bonus] })
                    "
                  >
                    {{
                      'pages.campaignDetails.goals.cell.optionalDelivered'
                        | translate: { count: cell.optionalDelivered }
                    }}
                  </button>
                } @else {
                  {{
                    'pages.campaignDetails.goals.cell.optionalDelivered' | translate: { count: cell.optionalDelivered }
                  }}
                }
              </span>
            } @else if (!hasBar) {
              <!-- An invisible copy of the Deliverables bar, count and chip under the text keeps the column as wide in both lenses. -->
              <div class="grid items-center" data-testid="campaign-details-goals-no-kpi-cell">
                <span class="col-start-1 row-start-1 text-xs text-shikenso-purple-base-60">
                  {{ 'pages.campaignDetails.goals.cell.noKpiGoals' | translate }}
                </span>
                @if (cell.lenses[EGoalsLens.deliverables].hasGoals) {
                  <app-slot-bar
                    class="invisible col-start-1 row-start-1"
                    data-testid="campaign-details-goals-width-keeper"
                    [slots]="[]"
                    [capacity]="columnLayouts().get(column.id)?.capacity ?? 0"
                  >
                    <span
                      class="shrink-0 whitespace-nowrap text-sm tabular-nums"
                      [style.min-width.ch]="countWidth(cell)"
                    >
                      {{ cell.lenses[EGoalsLens.deliverables].done }}/{{ cell.required }}
                    </span>
                    @if (cell.extraSlots.length) {
                      <span class="shrink-0 whitespace-nowrap rounded-full px-2 py-px text-xs">
                        {{ 'pages.campaignDetails.goals.notes.extra' | translate: { count: cell.extraSlots.length } }}
                      </span>
                    }
                  </app-slot-bar>
                }
              </div>
            } @else {
              <app-hover-card
                #hoverCard
                data-testid="campaign-details-goals-cell"
                [disabled]="!view?.summary && !view?.links?.length"
              >
                <!-- Block tooltips open away from the card, so they never cover its links. -->
                <app-slot-bar
                  [slots]="view?.bar ?? []"
                  [capacity]="columnLayouts().get(column.id)?.capacity ?? 0"
                  [tooltipPosition]="hoverCard.position()?.above ? 'below' : 'above'"
                  (slotClick)="onSlotClick(cell, $event)"
                >
                  @if (canOpenContents()) {
                    <button
                      type="button"
                      class="shrink-0 whitespace-nowrap text-left text-sm tabular-nums hover:underline focus-visible:underline"
                      data-testid="campaign-details-goals-cell-contents"
                      [style.min-width.ch]="countWidth(cell)"
                      [attr.aria-label]="getCellAriaLabel(row, cell, view?.summary ?? '')"
                      (click)="openContents(row, cell.ctaId)"
                    >
                      {{ cell.lenses[lens()].done }}/{{ cell.required }}
                    </button>
                  } @else {
                    <!-- Focusable when it has a summary: without Contents links or clickable blocks, the only way to its card by keyboard. -->
                    <span
                      class="shrink-0 whitespace-nowrap text-sm tabular-nums focus-visible:underline"
                      [style.min-width.ch]="countWidth(cell)"
                      [attr.tabindex]="view?.summary ? 0 : null"
                    >
                      {{ cell.lenses[lens()].done }}/{{ cell.required }}
                      @if (view?.summary) {
                        <span class="sr-only">{{ view?.summary }}</span>
                      }
                    </span>
                  }
                  @if (cell.extraSlots.length) {
                    @if (canOpenContents()) {
                      <button
                        type="button"
                        class="shrink-0 whitespace-nowrap rounded-full bg-shikenso-pink-base-24 px-2 py-px text-xs text-shikenso-pink-base hover:underline focus-visible:underline"
                        data-testid="campaign-details-goals-extra-link"
                        (click)="openContents(row, cell.ctaId, { matchingTypes: [EMatchingType.bonus] })"
                      >
                        {{ 'pages.campaignDetails.goals.notes.extra' | translate: { count: cell.extraSlots.length } }}
                      </button>
                    } @else {
                      <span
                        class="shrink-0 whitespace-nowrap rounded-full bg-shikenso-pink-base-24 px-2 py-px text-xs text-shikenso-pink-base"
                      >
                        {{ 'pages.campaignDetails.goals.notes.extra' | translate: { count: cell.extraSlots.length } }}
                      </span>
                    }
                  }
                </app-slot-bar>
                <ng-template hoverCardContent>
                  @if (view?.summary) {
                    <span class="text-xs text-shikenso-purple-base first-letter:uppercase">{{ view?.summary }}</span>
                  }
                  @for (link of view?.links ?? []; track link.key) {
                    <button
                      type="button"
                      class="text-left text-xs font-medium text-shikenso-pink-base hover:underline focus-visible:underline"
                      data-testid="campaign-details-goals-card-link"
                      [attr.data-link]="link.key"
                      (click)="openContents(row, cell.ctaId, link.link)"
                    >
                      {{ 'pages.campaignDetails.goals.card.' + link.key | translate: link.params }}
                    </button>
                  }
                </ng-template>
              </app-hover-card>
            }
          }
        }
      </ng-template>

      <ng-template matrixExpandedDef let-row let-column="column">
        @if (column.kind === 'group') {
          @let cell = row.cells[column.id];
          @let view = cellViews().get(cellKey(cell));
          <div
            class="flex flex-col gap-2"
            data-testid="campaign-details-goals-sub-rows"
            [style.min-height.px]="cell.subRowCount * 40"
          >
            @if (cell.lenses[lens()].hasGoals) {
              @for (subRow of view?.subRows ?? []; track subRow.id) {
                <div class="flex flex-col gap-1" data-testid="campaign-details-goals-sub-row">
                  <!-- w-0 + min-w-full: long names truncate instead of widening the column (it sizes to its cells). -->
                  <div class="flex w-0 min-w-full items-baseline gap-1 text-xs text-shikenso-purple-base-60">
                    <span class="min-w-0 truncate" [matTooltip]="subRow.name">{{ subRow.name }}</span>
                    <span class="shrink-0" aria-hidden="true">·</span>
                    @if (
                      subRow.deliverableType &&
                      canOpenDeliverablesLink({ ctaId: cell.ctaId, deliverableType: subRow.deliverableType, row })
                    ) {
                      <button
                        type="button"
                        class="shrink-0 whitespace-nowrap tabular-nums hover:text-shikenso-purple-base hover:underline focus-visible:underline"
                        data-testid="campaign-details-goals-sub-row-deliverables-link"
                        (click)="openDeliverables({ ctaId: cell.ctaId, deliverableType: subRow.deliverableType, row })"
                      >
                        {{ subRow.label }}
                      </button>
                    } @else {
                      <span class="shrink-0 whitespace-nowrap tabular-nums">{{ subRow.label }}</span>
                    }
                  </div>
                  <app-slot-bar
                    size="thin"
                    [slots]="subRow.bar"
                    [capacity]="columnLayouts().get(column.id)?.capacity ?? 0"
                    (slotClick)="onSubRowSlotClick(subRow, $event)"
                  />
                </div>
              }
            }
          </div>
        }
      </ng-template>

      <ng-template matrixFooterDef let-column let-collapsed="collapsed">
        @switch (column.kind) {
          @case ('creator') {
            <div class="flex flex-col">
              <span class="text-sm font-medium">{{
                'pages.campaignDetails.goals.footer.allCreators' | translate
              }}</span>
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.campaignDetails.goals.footer.campaignTotal' | translate }}
              </span>
            </div>
          }
          @case ('plan') {
            @let planTotalLabel =
              (lens() === EGoalsLens.kpis
                ? 'pages.campaignDetails.goals.footer.goalsOnTargetOf'
                : 'pages.campaignDetails.goals.footer.groupsCompleteOf'
              ) | translate: { done: planTotal().complete, total: planTotal().total };
            <div class="text-sm tabular-nums" data-testid="campaign-details-goals-plan-total">
              @if (lens() === EGoalsLens.deliverables && canOpenDeliverablesLink()) {
                <!-- Named "3/8 groups complete": the name starts with the visible text (voice control). -->
                <button
                  type="button"
                  class="hover:underline focus-visible:underline"
                  data-testid="campaign-details-goals-plan-total-deliverables-link"
                  [matTooltip]="planTotalLabel"
                  (click)="openDeliverables()"
                >
                  {{ planTotal().complete }}/{{ planTotal().total }}
                  <span class="sr-only">{{ 'pages.campaignDetails.goals.footer.groupsComplete' | translate }}</span>
                </button>
              } @else {
                <span aria-hidden="true" [matTooltip]="planTotalLabel">
                  {{ planTotal().complete }}/{{ planTotal().total }}
                </span>
                <span class="sr-only">{{ planTotalLabel }}</span>
              }
            </div>
          }
          @default {
            @let footer = column.group.footer[lens()];
            @let summary = footerSummaries().get(column.id)!;
            @if (collapsed) {
              @if (footer.creatorsInPlan) {
                <span
                  class="text-[10px] tabular-nums text-shikenso-purple-base-60"
                  data-testid="campaign-details-goals-collapsed-percent"
                >
                  {{ footer.percent }}%
                </span>
              }
            } @else if (footer.creatorsInPlan) {
              @let barWidth = columnLayouts().get(column.id)?.barWidth ?? 0;
              <div class="flex flex-col gap-1.5" data-testid="campaign-details-goals-column-footer">
                <!-- No blocks above (e.g. "every stream" before the first stream): no bar either, only the percent. -->
                @if (barWidth) {
                  <app-stacked-bar [segments]="footer.segments" [total]="footer.required" [width]="barWidth">
                    <span class="shrink-0 text-sm tabular-nums">{{ footer.percent }}%</span>
                  </app-stacked-bar>
                } @else {
                  <span class="text-sm tabular-nums">{{ footer.percent }}%</span>
                }
                <!-- w-0 + min-w-full: the line wraps instead of widening the column (it sizes to its cells). -->
                <span class="w-0 min-w-full text-xs text-shikenso-purple-base-60">
                  @if (lens() === EGoalsLens.kpis) {
                    {{
                      'pages.campaignDetails.goals.footer.onTarget'
                        | translate: { done: footer.fulfilled, total: footer.required }
                    }}
                    ·
                    {{
                      'pages.campaignDetails.goals.footer.creatorsOnTarget'
                        | translate: { done: footer.completeCreators, total: footer.creatorsInPlan }
                    }}
                  } @else {
                    @if (summary.link && canOpenContents()) {
                      <button
                        type="button"
                        class="hover:text-shikenso-purple-base hover:underline focus-visible:underline"
                        data-testid="campaign-details-goals-footer-contents-link"
                        (click)="openContents(null, column.id, summary.link)"
                      >
                        {{ summary.labelKey | translate: summary.params }}
                      </button>
                    } @else {
                      {{ summary.labelKey | translate: summary.params }}
                    }
                    ·
                    {{
                      'pages.campaignDetails.goals.footer.creatorsComplete'
                        | translate: { done: footer.completeCreators, total: footer.creatorsInPlan }
                    }}
                  }
                </span>
              </div>
            } @else if (lens() === EGoalsLens.kpis) {
              <span class="text-xs text-shikenso-purple-base-60">
                {{ 'pages.campaignDetails.goals.header.noKpiGoals' | translate }}
              </span>
            }
          }
        }
      </ng-template>
    </app-matrix-grid>
  }
</div>

<ng-template #sortIcon let-key>
  @if (shownSort()?.key === key) {
    <app-icon
      src="/icons/arrow-up.svg"
      alt=""
      color="var(--shikenso-pink-base)"
      class="block"
      [class.rotate-180]="shownSort()?.direction === 'desc'"
      [size]="12"
    />
  } @else {
    <app-icon src="/icons/chevron-sort.svg" alt="" color="var(--shikenso-gray-500)" [size]="12" />
  }
</ng-template>

<float-ui-content #legendPopover class="z-50">
  <div class="flex max-w-72 flex-col gap-3 p-4" data-testid="campaign-details-goals-legend">
    <p class="text-sm text-shikenso-purple-base">{{ 'pages.campaignDetails.goals.description' | translate }}</p>
    <app-status-legend [items]="legendItems()" footnote="pages.campaignDetails.goals.legend.footnote" />
  </div>
</float-ui-content>
